package dev.lingvo.site.pages.app.projects.slug.review

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.MAX_REVIEW_BATCH
import dev.lingvo.shared.ReviewAction
import dev.lingvo.shared.ReviewItemDto
import dev.lingvo.shared.TranslationDto
import dev.lingvo.shared.SourceChangeDto
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.components.review.Decision
import dev.lingvo.site.components.review.ReviewSourceConflict
import dev.lingvo.site.components.review.reviewSourceClaimAvailable
import dev.lingvo.site.components.editor.unavailableSourceReviewMessage
import dev.lingvo.site.components.review.ReviewBatchApprove
import dev.lingvo.site.components.review.ReviewBatchCounts
import dev.lingvo.site.components.review.ReviewBatchReceipt
import dev.lingvo.site.components.review.ReviewCard
import dev.lingvo.site.components.review.ReviewHeader
import dev.lingvo.site.components.review.ReviewPaging
import dev.lingvo.site.components.review.ReviewFeedback
import dev.lingvo.site.components.review.ReviewRequestChangesForm
import dev.lingvo.site.components.review.ReviewRequestChangesTrigger
import dev.lingvo.site.components.review.ReviewScope
import dev.lingvo.site.components.review.ReviewScopeControl
import dev.lingvo.site.components.review.ReviewShortcuts
import dev.lingvo.site.components.review.ReviewStaleConflict
import dev.lingvo.site.components.review.ReviewApprovalButton
import dev.lingvo.site.components.review.applyDecision
import dev.lingvo.site.components.review.changedSinceApproval
import dev.lingvo.site.components.review.decide
import dev.lingvo.site.components.review.reviewApprovalAvailable
import dev.lingvo.site.components.review.reviewBatchEligible
import dev.lingvo.site.components.review.reviewPayloadIsRepresentable
import dev.lingvo.site.components.review.reviewLocaleOptions
import dev.lingvo.site.components.review.reviewQueueScope
import dev.lingvo.site.components.review.reviewSubtitle
import dev.lingvo.site.components.review.runReviewAction
import dev.lingvo.site.components.review.shouldPrefetchNextScreenful
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LChevronRightIcon
import dev.lingvo.site.ds.LChord
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LGlobeIcon
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.chordKeys
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap

/**
 * The review lane: one string at a time, full width.
 *
 * A queue, not a grid. A grid invites skimming, and the point of review is that somebody actually
 * read the string.
 *
 * **A gate rather than an absolute.** The rule this lane protects used to be enforced by refusing
 * bulk approval outright, which also made a reviewer who had genuinely read a screenful press the
 * same button forty times. There is still no select-all grid; there is one batch action, and it is
 * allowed to exist only because it carries the whole gate: the exact strings on this screen at the
 * versions that were read, a server-side QA re-evaluation of every one of them, a preview built
 * from that same evaluation with nothing written, a per-item receipt that explains every skip, and
 * audit provenance identical to deciding each string on its own. `ReviewRoutes.kt` states the same
 * seven parts from the server's side; `ReviewBatchApprove.kt` implements this side of it.
 *
 * Keyboard-first: `a` approve, `r` request changes, `s` skip — this lane's own chords, shown beside
 * the buttons because nothing else advertises them. `⌘⇧Enter` is the one that carries over from the
 * editor, and it does the same thing here.
 */
@Page("/app/projects/{slug}/review/{locale}")
@Layout(".components.layouts.AppLayout")
@Composable
fun ReviewLanePage() {
    val ctx = rememberPageContext()
    ReviewLaneScreen(
        slug = ctx.route.params["slug"].orEmpty(),
        locale = ctx.route.params["locale"].orEmpty(),
    )
}

/**
 * The coordinator: page state, and where each piece of it is drawn.
 *
 * Split from the route so the lane can be mounted whole in a browser test, the way the editor's
 * screen is — the page itself is the two route parameters and nothing else.
 */
@Composable
internal fun ReviewLaneScreen(slug: String, locale: String) {
    val scope = rememberCoroutineScope()

    var refreshKey by remember { mutableStateOf(0) }
    var reviewScope by remember(slug, locale) { mutableStateOf(ReviewScope.ALL) }
    var notice by remember { mutableStateOf<String?>(null) }
    var note by remember { mutableStateOf("") }
    var asking by remember { mutableStateOf(false) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var receipt by remember(slug, locale) { mutableStateOf<ReviewBatchCounts?>(null) }
    // How far this sitting has got. Counted here rather than read off the queue because the queue
    // shrinks as it is decided: "12 of 40" is only a fact if something remembers the 40.
    val feedback = remember(slug, locale) { ReviewFeedback() }
    var startingTotal by remember(slug, locale) { mutableStateOf(0) }
    // The string as it stands, when an approval was refused because it moved. Non-null draws the
    // conflict card in place of the decision buttons: the choice is between two texts, so offering
    // Approve and Send back beside it would be offering a decision on a version that no longer exists.
    var stale by remember { mutableStateOf<TranslationDto?>(null) }
    var sourceConflict by remember(slug, locale) { mutableStateOf<SourceChangeDto?>(null) }
    var sourceUnavailable by remember(slug, locale) { mutableStateOf(false) }
    var sourceAwareMode by remember(slug, locale) { mutableStateOf(Session.serverFeatures.sourceAwareReview) }
    // The cursor indexes the *scoped* list, so a scope change or a reload must put it back to the
    // top: keeping it would land on an unrelated string, or past the end.
    var cursor by remember(slug, locale, refreshKey, reviewScope) { mutableStateOf(0) }
    val paging = remember(slug, locale, refreshKey) { ReviewPaging() }

    val reloadable = loadReloadable(slug, locale, reloadToken = refreshKey) {
        if (Session.serverFeatures.sourceAwareReview) sourceAwareMode = true
        feedback.readQueue(slug, locale, refreshKey)
    }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        when (val state = reloadable.state) {
            Async.Loading -> {
                PageHeader(
                    t("app.projects.slug.review.locale.review", "Review"),
                    t("app.projects.slug.review.locale.loading_the_queue", "Loading the queue."),
                )
                LoadingState(
                    rows = 3,
                    label = t("app.projects.slug.review.locale.loading_strings_to_review", "Loading strings to review"),
                )
            }
            is Async.Failure -> {
                PageHeader(t("app.projects.slug.review.locale.review", "Review"), "")
                AsyncFailureState(
                    state,
                    subject = t("app.projects.slug.review.locale.this_review_queue", "this review queue"),
                    onRetry = { refreshKey++ },
                )
            }
            is Async.Success -> {
                val read = state.value
                val queue = read.queue
                val countsRead = paging.latestRead?.takeIf { it.basis.order > read.basis.order } ?: read
                val remaining = feedback.remaining(countsRead)
                val currentReadRequired = paging.countsFailed || !feedback.hasCurrentRead(countsRead)
                val mayDecide = queue.canApprove && countsRead.queue.canApprove && !currentReadRequired
                val language = localeInfo(locale).englishName
                val items = queue.items + paging.appended
                LaunchedEffect(Session.serverFeatures.sourceAwareReview, items) {
                    if (Session.serverFeatures.sourceAwareReview || items.any { it.sourceSnapshot != null }) sourceAwareMode = true
                }
                val pending = feedback.pending(items)
                val revisedCount = pending.count(::changedSinceApproval)
                val qaCleanCount = pending.count { !it.blocked }
                val visible = reviewQueueScope(items, reviewScope)
                val pendingVisible = reviewQueueScope(pending, reviewScope)
                val itemIndex = (cursor until visible.size).firstOrNull { visible[it] in pendingVisible }
                val item = itemIndex?.let(visible::get)
                val pendingCursor = item?.let(pendingVisible::indexOf) ?: pendingVisible.size
                // Read at the moment a suspended batch resumes, not captured when it started: the
                // queue reloads underneath the run, and with it the server's answer about whether
                // this reviewer may still decide here.
                val canApproveNow = rememberUpdatedState(mayDecide)

                LaunchedEffect(paging, read) {
                    // A retained queue belongs to the previous reload. Do not prefetch its cursor
                    // into the new paging store while the authoritative reload is still pending.
                    if (read.basis.reloadToken == refreshKey) paging.seed(queue.nextCursor)
                    if (startingTotal == 0) startingTotal = queue.total
                }
                LaunchedEffect(paging, cursor, visible.size, paging.more, paging.loading, paging.failed) {
                    if (!paging.failed && shouldPrefetchNextScreenful(cursor, visible.size, paging.more != null, paging.loading)) {
                        paging.fetch(slug, locale, feedback.captureRead(refreshKey), feedback)
                    }
                }

                ReviewHeader(
                    title = tf(
                        "app.projects.slug.review.locale.review_language",
                        "Review {language}",
                        "language" to language,
                    ),
                    subtitle = reviewSubtitle(remaining, pendingVisible.size, pendingCursor, reviewScope),
                    locale = locale,
                    language = language,
                    localeOptions = reviewLocaleOptions(slug, locale),
                    qaCleanPending = feedback.qaCleanRemaining(countsRead),
                    qaCleanIsFloor = countsRead.queue.total > MAX_REVIEW_BATCH,
                    decided = feedback.decided,
                    queueTotal = maxOf(startingTotal, queue.total),
                    actions = {
                        ReviewBatchApprove(
                            slug = slug,
                            locale = locale,
                            eligible = reviewBatchEligible(pendingVisible, sourceAwareMode),
                            canApprove = mayDecide,
                            // Re-read, never captured: the authority this control is gated on is the
                            // server's answer for this locale, and the queue reloads under it.
                            currentlyCanApprove = {
                                canApproveNow.value && feedback.hasCurrentRead(countsRead) && pendingVisible.none(feedback::acknowledged)
                            },
                            onRefused = { refusal ->
                                notice = null
                                message = refusal
                            },
                            onFinished = { counts ->
                                counts.acknowledged.forEach { feedback.acknowledge(it) }
                                if (counts.unconfirmed) feedback.requireCurrentRead()
                                receipt = counts
                                notice = null
                                message = null
                                // Retained: the last good queue stays on screen while the new one
                                // is fetched, so the receipt above it is not talking about a page
                                // that has just been replaced by skeleton bars.
                                refreshKey++
                            },
                            guardedMode = sourceAwareMode,
                        )
                    },
                )

                ReviewScopeControl(
                    scope = reviewScope,
                    onScopeChange = {
                        reviewScope = it
                        message = null
                        notice = null
                        stale = null
                        sourceConflict = null
                        sourceUnavailable = false
                    },
                    loaded = pending.size,
                    revisedCount = revisedCount,
                    qaCleanCount = qaCleanCount,
                )

                // A failed refresh belongs beside the last good data, not in place of it.
                reloadable.refreshError?.let { failure ->
                    LAlert(
                        t("app.projects.slug.review.locale.the_queue_did_not_refresh", "The queue did not refresh"),
                        failure.readable(),
                        variant = LAlertVariant.WARNING,
                        dense = true,
                    )
                }

                receipt?.let { counts -> ReviewBatchReceipt(counts) { receipt = null } }

                if (paging.countsFailed) {
                    LAlert(
                        t("app.projects.slug.review.locale.queue_counts_need_a_refresh", "Queue counts need a refresh"),
                        t("app.projects.slug.review.locale.reload_to_confirm_remaining", "A decision completed during the page read. Reload the queue to confirm how much remains."),
                        variant = LAlertVariant.WARNING,
                        live = true,
                        dense = true,
                    )
                }
                if (currentReadRequired) {
                    LButton(
                        t("app.projects.slug.review.locale.reload_the_queue", "Reload the queue"),
                        { refreshKey++ },
                        variant = LButtonVariant.SECONDARY,
                        enabled = !reloadable.refreshing && !paging.loading,
                        loading = reloadable.refreshing || paging.loading,
                    )
                }

                if (!queue.canApprove) {
                    LAlert(
                        t("app.projects.slug.review.locale.you_can_read_this_queue_but", "You can read this queue but not decide in it"),
                        tf(
                            "app.projects.slug.review.locale.only_reviewers_can_approve_language",
                            "Only reviewers can approve {language}. An owner or admin can make you " +
                                "a reviewer — it costs no billable seat.",
                            "language" to language,
                        ),
                        variant = LAlertVariant.INFO,
                    )
                }

                // News and failure are not the same colour. The warning treatment is reserved for
                // what the reviewer should fix; losing a race is neither their fault nor their job.
                // Both appear in response to a decision the reviewer just made, on a surface whose
                // next item has already replaced the one they acted on. Without `live` the outcome
                // is only available to someone who happens to look at the right part of the page.
                notice?.let { text ->
                    LAlert(
                        t("app.projects.slug.review.locale.already_reviewed", "Already reviewed"),
                        text,
                        variant = LAlertVariant.INFO,
                        live = true,
                        dense = true,
                        onDismiss = { notice = null },
                    )
                }
                message?.let { text ->
                    LAlert(
                        t("app.projects.slug.review.locale.that_didnt_go_through", "That didn't go through"),
                        text,
                        variant = LAlertVariant.WARNING,
                        live = true,
                        dense = true,
                        onDismiss = { message = null },
                    )
                }

                if (paging.failed) {
                    LAlert(
                        t("app.projects.slug.review.locale.couldnt_load_next_strings", "Couldn't load the next strings"),
                        t("app.projects.slug.review.locale.queue_incomplete_retry", "The remaining strings have not loaded. Retry to continue this queue."),
                        variant = LAlertVariant.WARNING,
                        live = true,
                        dense = true,
                        action = {
                            LButton(
                                t("app.projects.slug.review.locale.retry_next_strings", "Retry next strings"),
                                { scope.launch { paging.fetch(slug, locale, feedback.captureRead(refreshKey), feedback) } },
                                variant = LButtonVariant.SECONDARY,
                                loading = paging.loading,
                            )
                        },
                    )
                }

                when {
                    item == null && pending.isEmpty() && remaining == 0 -> LEmptyState(
                        title = t("app.projects.slug.review.locale.nothing_waiting_for_review", "Nothing waiting for review."),
                        description = when {
                            feedback.readStayedStable(countsRead.basis) && !currentReadRequired -> tPlural(
                                "app.projects.slug.review.locale.count.approved_last_week",
                                countsRead.queue.approvedLastWeek,
                                "{count} string approved in the last seven days.",
                                "{count} strings approved in the last seven days.",
                            )
                            feedback.decided > countsRead.basis.acknowledged && receipt?.unconfirmed != true -> t(
                                "app.projects.slug.review.locale.review_decisions_saved",
                                "Your review decisions are saved. Reload to see the latest seven-day approval count.",
                            )
                            else -> t(
                                "app.projects.slug.review.locale.recent_approvals_need_fresh_read",
                                "Reload the queue to confirm the latest seven-day approval count.",
                            )
                        },
                        icon = LEmptyStateIcon.SUCCESS,
                        // Full width, like the banner above it and every card it stands in for. Left
                        // at its own 32rem it was a 464px card parked in a 1128px column with two
                        // thirds of the ground beside it empty, which reads as a stray card rather
                        // than as the answer to the question the reader just asked.
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        if (!currentReadRequired) {
                            LButton(
                                t("app.projects.slug.review.locale.reload_the_queue", "Reload the queue"),
                                { refreshKey++ },
                                variant = LButtonVariant.SECONDARY,
                                enabled = !reloadable.refreshing,
                                loading = reloadable.refreshing,
                            )
                        }
                        LLinkButton(
                            t("app.projects.slug.review.locale.open_the_editor", "Open the editor"),
                            "/app/projects/$slug/translate/$locale",
                            variant = LButtonVariant.SECONDARY,
                            // The same glyph the Translate destination carries, so the button and the
                            // place it goes are the same thing to the eye.
                            leadingIcon = { LGlobeIcon() },
                        )
                    }
                    item == null && paging.failed -> Unit
                    item == null && paging.more != null -> LoadingState(
                        rows = 1,
                        label = t("app.projects.slug.review.locale.loading_the_next_screenful", "Loading the next screenful"),
                    )
                    item == null && reviewScope != ReviewScope.ALL -> LEmptyState(
                        title = t(
                            "app.projects.slug.review.locale.nothing_in_this_scope_is_waiting",
                            "Nothing in this scope is waiting.",
                        ),
                        description = t(
                            "app.projects.slug.review.locale.the_rest_of_the_queue_is",
                            "The rest of the queue is still there.",
                        ),
                        icon = LEmptyStateIcon.SEARCH,
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        LButton(
                            t("app.projects.slug.review.locale.review_everything_waiting", "Review everything waiting"),
                            { reviewScope = ReviewScope.ALL },
                        )
                    }
                    item == null -> LEmptyState(
                        title = t("app.projects.slug.review.locale.thats_everything_waiting", "That's everything waiting."),
                        description = t(
                            "app.projects.slug.review.locale.reload_to_pick_up_anything_submitted",
                            "Reload to pick up anything submitted since you started.",
                        ),
                        icon = LEmptyStateIcon.SUCCESS,
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        LButton(
                            t("app.projects.slug.review.locale.reload_the_queue", "Reload the queue"),
                            { refreshKey++ },
                        )
                    }
                    else -> {
                        val approve = {
                            scope.launch {
                                if (!canApproveNow.value || !feedback.hasCurrentRead(countsRead) || feedback.acknowledged(item)) return@launch
                                runReviewAction(onBusyChange = { busy = it }) {
                                    val outcome = decide(slug, locale, item, ReviewAction.APPROVE, null, sourceAwareMode)
                                    if (outcome is Decision.Done) feedback.acknowledge(item)
                                    applyDecision(
                                        outcome,
                                        onAdvance = { cursor = checkNotNull(itemIndex) + 1 },
                                        onNotice = { notice = it },
                                        onProblem = { message = it },
                                        onStale = { stale = it },
                                        onSourceChanged = { sourceConflict = it },
                                        onSourceUnavailable = { sourceUnavailable = true },
                                    )
                                }
                            }
                            Unit
                        }
                        // Each chord carries the exact guard its button carries, so a keystroke can
                        // never do what the click correctly refuses. `r` opens the note and does not
                        // submit — the note is required, and a bare `r` that sent a string back with
                        // no explanation is how review turns into resentment.
                        ReviewShortcuts(
                            onApprove = approve.takeIf {
                                sourceConflict == null && !sourceUnavailable && reviewApprovalAvailable(item, null, mayDecide, busy, asking, sourceAwareMode)
                            },
                            onRequestChanges = { asking = true }.takeIf { mayDecide && !busy && !asking && sourceConflict == null && !sourceUnavailable && reviewSourceClaimAvailable(item, guardedMode = sourceAwareMode) },
                            onSkip = { message = null; notice = null; stale = null; sourceConflict = null; sourceUnavailable = false; cursor = checkNotNull(itemIndex) + 1; Unit }
                                .takeIf { !busy && !asking && stale == null },
                        )

                        ReviewCard(item, slug, queue.sourceLocale ?: "und", locale)

                        if (!reviewSourceClaimAvailable(item, guardedMode = sourceAwareMode) || sourceUnavailable) LAlert(
                            t("app.projects.slug.review.locale.source_aware_review_is_unavailable", "Source-aware review is unavailable"),
                            unavailableSourceReviewMessage(), variant = LAlertVariant.INFO)

                        sourceConflict?.let { change ->
                            ReviewSourceConflict(change, mayDecide, busy,
                                onApprove = { fresh -> scope.launch {
                                    if (!canApproveNow.value || !feedback.hasCurrentRead(countsRead) || feedback.acknowledged(fresh)) return@launch
                                    runReviewAction(onBusyChange = { busy = it }) {
                                        val outcome = decide(slug, locale, fresh, ReviewAction.APPROVE, null, true)
                                        if (outcome is Decision.Done) feedback.acknowledge(fresh, item)
                                        applyDecision(outcome,
                                            onAdvance = { sourceConflict = null; cursor = checkNotNull(itemIndex) + 1 },
                                            onNotice = { notice = it }, onProblem = { message = it }, onStale = { stale = it },
                                            onSourceChanged = { sourceConflict = it }, onSourceUnavailable = { sourceUnavailable = true })
                                    }
                                } },
                                onReload = { sourceConflict = null; sourceUnavailable = false; refreshKey++ })
                        }

                        stale?.let { current ->
                            ReviewStaleConflict(
                                item = item,
                                current = current,
                                locale = locale,
                                canApprove = mayDecide,
                                busy = busy,
                                onApprove = {
                                    scope.launch {
                                        if (!canApproveNow.value || !feedback.hasCurrentRead(countsRead)) return@launch
                                        runReviewAction(onBusyChange = { busy = it }) {
                                            // Re-submitted against the version now on screen, so
                                            // the guard that refused the first attempt is satisfied
                                            // by the text the reviewer has actually just read.
                                            val submitted = item.copy(updatedAt = current.updatedAt, targetRevision = current.revision)
                                            val outcome = decide(
                                                slug, locale,
                                                submitted,
                                                ReviewAction.APPROVE, null,
                                                sourceAwareMode,
                                            )
                                            if (outcome is Decision.Done) feedback.acknowledge(submitted, item)
                                            applyDecision(
                                                outcome,
                                                onAdvance = { stale = null; cursor = checkNotNull(itemIndex) + 1 },
                                                onNotice = { notice = it },
                                                onProblem = { message = it },
                                                onStale = { stale = it },
                                                onSourceChanged = { sourceConflict = it; stale = null },
                                                onSourceUnavailable = { sourceUnavailable = true },
                                            )
                                        }
                                    }
                                },
                                onReload = { stale = null; message = null; refreshKey++ },
                                guardedMode = sourceAwareMode,
                            )
                        }

                        if (asking) {
                            ReviewRequestChangesForm(
                                note = note,
                                onNoteChange = { note = it },
                                busy = busy || currentReadRequired,
                                noteLabel = t("app.projects.slug.review.locale.what_needs_changing", "What needs changing?"),
                                // Required, and the button says so rather than a 400 doing it. An
                                // unexplained rejection is how review turns into resentment.
                                placeholder = t("app.projects.slug.review.locale.this_is_the_checkout_button_not", "This is the checkout button, not the cart."),
                                sendLabel = t("app.projects.slug.review.locale.send_back", "Send back"),
                                cancelLabel = t("app.projects.slug.review.locale.cancel", "Cancel"),
                                onSend = {
                                    scope.launch {
                                        if (!canApproveNow.value || !feedback.hasCurrentRead(countsRead) || feedback.acknowledged(item)) return@launch
                                        runReviewAction(onBusyChange = { busy = it }) {
                                            val outcome =
                                                decide(slug, locale, item, ReviewAction.REQUEST_CHANGES, note, sourceAwareMode)
                                            if (outcome is Decision.Done) feedback.acknowledge(item)
                                            applyDecision(
                                                outcome,
                                                // A raced string must not leave the typed note stranded
                                                // in an open form either.
                                                onAdvance = { note = ""; asking = false; cursor = checkNotNull(itemIndex) + 1 },
                                                onNotice = { notice = it },
                                                onProblem = { message = it },
                                                onStale = { stale = it },
                                                onSourceChanged = { sourceConflict = it; asking = false },
                                                onSourceUnavailable = { sourceUnavailable = true },
                                            )
                                        }
                                    }
                                },
                                onCancel = { asking = false },
                            )
                        } else if (stale == null && sourceConflict == null && !sourceUnavailable) {
                            // The chords, beside the buttons they fire. Nothing else in the lane
                            // advertised them, so a translator arriving with the editor's muscle
                            // memory had no way to learn they existed.
                            // Wrapping, because this row is three decisions and their chords and a
                            // 390px screen cannot hold them on one line: unwrapped, "Request changes"
                            // was sliced by the viewport edge and "Skip" was off it entirely — two of
                            // the three decisions the lane exists for, unreachable on a phone.
                            Row(
                                Modifier.gap(Space.SM).flexWrap(FlexWrap.Wrap),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                // One chip per decision — `a`, `r`, `s` — and each one inside its
                                // button's own non-wrapping row. Two things at once: a second chord
                                // beside Approve read as one four-key accelerator (keys inside a
                                // chord sit 4px apart, two chords 8px, so `[a] [⌘] [⇧] [Enter]` was
                                // a single run to the eye), and a chip that wraps away from its
                                // button lands beside the *next* decision and starts describing
                                // that one instead. `⌘⇧Enter` still approves — `ReviewShortcuts`
                                // binds it — and is advertised in the editor it carries over from.
                                DecisionWithChord("a") {
                                    ReviewApprovalButton(
                                        label = t("app.projects.slug.review.locale.approve", "Approve"),
                                        item = item,
                                        current = null,
                                        canApprove = mayDecide,
                                        busy = busy,
                                        onApprove = approve,
                                        guardedMode = sourceAwareMode,
                                    )
                                }
                                DecisionWithChord("r") {
                                    ReviewRequestChangesTrigger(
                                        label = t("app.projects.slug.review.locale.request_changes", "Request changes"),
                                        enabled = mayDecide && !busy && reviewSourceClaimAvailable(item, guardedMode = sourceAwareMode),
                                        onOpen = { asking = true },
                                    )
                                }
                                DecisionWithChord("s") {
                                    LButton(
                                        t("app.projects.slug.review.locale.skip", "Skip"),
                                        // Clears both banners: a stale "That didn't go through"
                                        // hanging over the *next* card reads as that card having
                                        // failed.
                                        { message = null; notice = null; cursor = checkNotNull(itemIndex) + 1 },
                                        enabled = !busy,
                                        variant = LButtonVariant.GHOST,
                                        trailingIcon = { LChevronRightIcon() },
                                    )
                                }
                            }
                            if (item.blocked) {
                                LMuted(t("app.projects.slug.review.locale.approving_is_blocked_until_the_qa", "Approving is blocked until the QA errors above are fixed."))
                            } else if (!reviewPayloadIsRepresentable(item)) {
                                LMuted(t("app.projects.slug.review.locale.approving_is_blocked_because_the_source", "Approving is blocked because the source and submitted value cannot be shown faithfully."))
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * One decision and the key that fires it, bound together so a wrap cannot separate them.
 *
 * The decision row wraps at 390 — it has to, or two of the three decisions are off the screen — and
 * a bare [LChord] in that row is its own flex item: the `r` chip broke onto the second line on its
 * own, landed to the left of Skip, and told the reader that Skip's key was `r`. A chip belongs to
 * exactly one button, so it travels with it.
 */
@Composable
private fun DecisionWithChord(key: String, decision: @Composable () -> Unit) {
    Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
        decision()
        LChord(chordKeys(key))
    }
}
